if (!customElements.get('arista-bogo-builder')) {
  customElements.define(
    'arista-bogo-builder',
    class AristaBogoBuilder extends HTMLElement {
      constructor() {
        super();
        this.selected = new Map();
        this.maxSelection = 2;
        this.previousCount = 0;
        this.storageVersion = 1;
        this.storageMaxAge = 12 * 60 * 60 * 1000;
        this.activeFilter = 'all';
        this.activeSort = 'recommended';
        this.filterTargetCount = 20;
        this.nextCollectionRank = 0;
        this.loadedUrls = new Set();
        this.seenProductIds = new Set();
        this.seenVariantIds = new Set();
        this.seenDomIds = new Set();
        this.isLoading = false;
        this.isEnsuringFilter = false;
        this.isEnsuringSort = false;
        this.handleClickBound = this.handleClick.bind(this);
        this.handleChangeBound = this.handleChange.bind(this);
        this.handleKeydownBound = this.handleKeydown.bind(this);
        this.scheduleStickyOffsetBound = this.scheduleStickyOffset.bind(this);
      }

      connectedCallback() {
        this.statusTitle = this.querySelector('[data-bogo-title]');
        this.message = this.querySelector('[data-bogo-message]');
        this.live = this.querySelector('[data-bogo-live]');
        this.meter = this.querySelector('[data-bogo-meter]');
        this.dock = this.querySelector('[data-bogo-dock]');
        this.count = this.querySelector('[data-bogo-count]');
        this.dockMessage = this.querySelector('[data-bogo-dock-message]');
        this.estimatedSaving = this.querySelector('[data-bogo-estimated-saving]');
        this.slotsContainer = this.querySelector('[data-bogo-slots]');
        this.submit = this.querySelector('[data-bogo-submit]');
        this.submitDefaultLabel = this.submit?.textContent.trim() || 'Add selected pair to cart';
        this.confetti = this.querySelector('[data-bogo-confetti]');
        this.celebration = this.querySelector('[data-bogo-celebration]');
        this.celebrationValue = this.querySelector('[data-bogo-celebration-value]');
        this.filters = this.querySelector('[data-bogo-filters]');
        this.sorts = this.querySelector('[data-bogo-sorts]');
        this.filterStatus = this.querySelector('[data-bogo-filter-status]');
        this.grid = this.querySelector('[data-bogo-grid]');
        this.emptyState = this.querySelector('[data-bogo-empty]');
        this.loader = this.querySelector('[data-bogo-loader]');
        this.loadStatus = this.querySelector('[data-bogo-load-status]');
        this.nextLink = this.querySelector('[data-bogo-next]');
        this.sentinel = this.querySelector('[data-bogo-sentinel]');
        this.watchTemplate = this.querySelector('[data-bogo-watch-template]');
        this.watchRail = this.querySelector('[data-bogo-watch-placement]');
        this.totalProducts = Number(this.loader?.dataset.totalProducts || this.grid?.dataset.bogoTotal || 0);

        this.addEventListener('click', this.handleClickBound);
        this.addEventListener('change', this.handleChangeBound);
        this.filters?.addEventListener('keydown', this.handleKeydownBound);

        this.activeFilter = this.initialFilterFromUrl();
        this.activeSort = this.initialSortFromUrl();
        this.indexRenderedCards();

        const collectionHandle = this.dataset.collectionHandle || 'buy1-get1';
        this.storageKey = `arista-bogo-selection:${collectionHandle}`;
        this.restoreSelection();
        this.updateState({ announce: false });
        this.setupInfiniteScroll();
        this.setupStickyBar();
        this.setFilter(this.activeFilter, { updateHistory: false });
        this.setSort(this.activeSort, { updateHistory: false });

        if ('ResizeObserver' in window && this.dock) {
          this.dockResizeObserver = new ResizeObserver(() => this.syncDockOffset());
          this.dockResizeObserver.observe(this.dock);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClickBound);
        this.removeEventListener('change', this.handleChangeBound);
        this.filters?.removeEventListener('keydown', this.handleKeydownBound);
        this.dockResizeObserver?.disconnect();
        this.infiniteObserver?.disconnect();
        this.fetchController?.abort();
        window.removeEventListener('scroll', this.scheduleStickyOffsetBound);
        window.removeEventListener('resize', this.scheduleStickyOffsetBound);
        this.stickyResizeObserver?.disconnect();
        if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
        if (this.celebrationTimer) window.clearTimeout(this.celebrationTimer);
        if (this.savingAnimationFrame) cancelAnimationFrame(this.savingAnimationFrame);
        this.clearDockOffset();
      }

      handleClick(event) {
        if (!(event.target instanceof Element)) return;

        const filterButton = event.target.closest('[data-bogo-filter]');
        if (filterButton) {
          this.setFilter(filterButton.dataset.bogoFilter || 'all', { focusButton: filterButton });
          return;
        }

        const sortButton = event.target.closest('[data-bogo-sort]');
        if (sortButton) {
          this.setSort(sortButton.dataset.bogoSort || 'recommended', { focusButton: sortButton });
          return;
        }

        if (event.target.closest('[data-bogo-celebration-close]')) {
          this.hideSavingCelebration();
          return;
        }

        const loadMore = event.target.closest('[data-bogo-next]');
        if (loadMore) {
          event.preventDefault();
          this.loadNextPage({ source: 'fallback' });
          return;
        }

        const selectButton = event.target.closest('[data-bogo-select]');
        if (selectButton) {
          const card = selectButton.closest('[data-bogo-card]');
          if (card) this.toggleCard(card);
          return;
        }

        const removeButton = event.target.closest('[data-bogo-remove]');
        if (removeButton) {
          this.removeSlot(Number(removeButton.dataset.bogoRemove));
          return;
        }

        const replaceButton = event.target.closest('[data-bogo-replace]');
        if (replaceButton) {
          this.replaceSlot(Number(replaceButton.dataset.bogoReplace));
          return;
        }

        if (event.target.closest('[data-bogo-submit]')) this.addSelectedToCart();
      }

      handleChange(event) {
        if (!(event.target instanceof Element)) return;
        const variantInput = event.target.closest('[data-bogo-variant]');
        const card = variantInput?.closest('[data-bogo-card]');
        if (card) this.updateSelectedVariant(card);
      }

      handleKeydown(event) {
        if (
          !(event.target instanceof Element) ||
          !event.target.matches('[data-bogo-filter], [data-bogo-sort]')
        ) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const isSort = event.target.matches('[data-bogo-sort]');
        const buttons = [
          ...(isSort ? this.querySelectorAll('[data-bogo-sort]') : this.querySelectorAll('[data-bogo-filter]')),
        ];
        if (buttons.length === 0) return;
        const currentIndex = buttons.indexOf(event.target);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = buttons.length - 1;
        else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;

        event.preventDefault();
        const nextButton = buttons[nextIndex];
        if (isSort) this.setSort(nextButton.dataset.bogoSort || 'recommended', { focusButton: nextButton });
        else this.setFilter(nextButton.dataset.bogoFilter || 'all', { focusButton: nextButton });
      }

      initialFilterFromUrl() {
        const allowedFilters = new Set(
          [...this.querySelectorAll('[data-bogo-filter]')].map((button) => button.dataset.bogoFilter)
        );
        try {
          const requestedFilter = new URL(window.location.href).searchParams.get('bogo_filter') || 'all';
          return allowedFilters.has(requestedFilter) ? requestedFilter : 'all';
        } catch (_error) {
          return 'all';
        }
      }

      initialSortFromUrl() {
        const allowedSorts = new Set(
          [...this.querySelectorAll('[data-bogo-sort]')].map((button) => button.dataset.bogoSort)
        );
        try {
          const requestedSort = new URL(window.location.href).searchParams.get('bogo_sort') || 'recommended';
          return allowedSorts.has(requestedSort) ? requestedSort : 'recommended';
        } catch (_error) {
          return 'recommended';
        }
      }

      filterLabel(filterKey) {
        return (
          this.querySelector(`[data-bogo-filter="${CSS.escape(filterKey)}"] span`)?.textContent?.trim() ||
          'Best Sellers'
        );
      }

      sortLabel(sortKey = this.activeSort) {
        return this.querySelector(`[data-bogo-sort="${CSS.escape(sortKey)}"]`)?.textContent?.trim() || 'Recommended';
      }

      setFilter(filterKey, { focusButton = null, updateHistory = true } = {}) {
        const button = this.querySelector(`[data-bogo-filter="${CSS.escape(filterKey)}"]`);
        if (!button) return;

        this.activeFilter = filterKey;
        this.querySelectorAll('[data-bogo-filter]').forEach((candidate) => {
          const isActive = candidate === button;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-selected', String(isActive));
          candidate.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        if (focusButton) {
          this.focusElement(focusButton);
          try {
            focusButton.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
          } catch (_error) {
            focusButton.scrollIntoView();
          }
        }

        if (updateHistory) this.updateViewHistory();
        this.applyFilter();
        if (filterKey !== 'all') this.ensureFilterCoverage();
      }

      setSort(sortKey, { focusButton = null, updateHistory = true } = {}) {
        const button = this.querySelector(`[data-bogo-sort="${CSS.escape(sortKey)}"]`);
        if (!button) return;

        this.activeSort = sortKey;
        this.querySelectorAll('[data-bogo-sort]').forEach((candidate) => {
          const isActive = candidate === button;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-checked', String(isActive));
          candidate.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        if (focusButton) {
          this.focusElement(focusButton);
          try {
            focusButton.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
          } catch (_error) {
            focusButton.scrollIntoView();
          }
        }

        if (updateHistory) this.updateViewHistory();
        this.applyFilter();
        if (sortKey !== 'recommended') this.ensureSortCoverage();
      }

      updateViewHistory() {
        try {
          const url = new URL(window.location.href);
          if (this.activeFilter === 'all') url.searchParams.delete('bogo_filter');
          else url.searchParams.set('bogo_filter', this.activeFilter);
          if (this.activeSort === 'recommended') url.searchParams.delete('bogo_sort');
          else url.searchParams.set('bogo_sort', this.activeSort);
          window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        } catch (_error) {
          // Filtering remains fully functional when URL history is unavailable.
        }
      }

      cardMatchesFilter(card, filterKey = this.activeFilter) {
        if (filterKey === 'all') return true;
        return String(card.dataset.bogoCategory || '')
          .split(/\s+/)
          .filter(Boolean)
          .includes(filterKey);
      }

      renderedCards() {
        return this.grid ? [...this.grid.querySelectorAll('.arista-bogo-card[data-product-id]')] : [];
      }

      visibleCards() {
        return this.renderedCards().filter((card) => this.cardMatchesFilter(card));
      }

      applyFilter({ announce = true } = {}) {
        this.applySort();
        const cards = this.renderedCards();
        let visibleCount = 0;
        cards.forEach((card) => {
          const visible = this.cardMatchesFilter(card);
          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });

        this.querySelectorAll('[data-bogo-filter-count]').forEach((countNode) => {
          const filterKey = countNode.dataset.bogoFilterCount || 'all';
          const count =
            filterKey === 'all' ? cards.length : cards.filter((card) => this.cardMatchesFilter(card, filterKey)).length;
          const catalogueComplete = !this.nextLink && !this.isLoading;
          countNode.textContent = count === 0 && !catalogueComplete ? '…' : String(count);

          const filterButton = countNode.closest('[data-bogo-filter]');
          if (filterButton && filterKey !== 'all') {
            filterButton.hidden = catalogueComplete && count === 0 && filterKey !== this.activeFilter;
          }
        });

        const catalogueComplete = !this.nextLink && !this.isLoading;
        if (catalogueComplete && visibleCount === 0 && this.activeFilter !== 'all') {
          this.setFilter('all', { updateHistory: true });
          return this.visibleCards().length;
        }

        const label = this.filterLabel(this.activeFilter);
        const sortLabel = this.sortLabel();
        if (this.filterStatus) {
          const loadingExactSort = this.activeSort !== 'recommended' && Boolean(this.nextLink);
          this.filterStatus.textContent = loadingExactSort
            ? `Loading every piece for exact ${sortLabel.toLowerCase()} order…`
            : `${visibleCount} ${label} ${visibleCount === 1 ? 'piece' : 'pieces'} shown, sorted by ${sortLabel.toLowerCase()}.`;
          this.filterStatus.setAttribute('aria-live', announce ? 'polite' : 'off');
        }
        if (this.emptyState) this.emptyState.hidden = visibleCount > 0 || Boolean(this.nextLink) || this.isLoading;
        this.placeWatchRail();
        return visibleCount;
      }

      applySort() {
        if (!this.grid) return;
        this.watchRail?.remove();
        const cards = this.renderedCards();
        const number = (card, key, fallback = 0) => {
          const value = Number(card.dataset[key]);
          return Number.isFinite(value) ? value : fallback;
        };
        const byCollectionOrder = (first, second) =>
          number(first, 'bogoCollectionRank', Number.MAX_SAFE_INTEGER) -
          number(second, 'bogoCollectionRank', Number.MAX_SAFE_INTEGER);

        cards.sort((first, second) => {
          if (this.activeSort === 'price-ascending') {
            return number(first, 'bogoSortPrice', Number.MAX_SAFE_INTEGER) -
              number(second, 'bogoSortPrice', Number.MAX_SAFE_INTEGER) || byCollectionOrder(first, second);
          }
          if (this.activeSort === 'price-descending') {
            return number(second, 'bogoSortPrice', -1) - number(first, 'bogoSortPrice', -1) ||
              byCollectionOrder(first, second);
          }
          if (this.activeSort === 'created-descending') {
            return number(second, 'bogoSortCreated', 0) - number(first, 'bogoSortCreated', 0) ||
              byCollectionOrder(first, second);
          }
          return byCollectionOrder(first, second);
        });
        cards.forEach((card) => this.grid.appendChild(card));
      }

      placeWatchRail() {
        if (!this.grid || !this.watchTemplate) return;

        const visibleCards = this.visibleCards();
        if (visibleCards.length < 12) {
          this.watchRail?.remove();
          return;
        }

        if (!this.watchRail) {
          this.watchRail = this.watchTemplate.content.firstElementChild?.cloneNode(true) || null;
        }
        if (!this.watchRail) return;

        const anchor = visibleCards[11];
        if (anchor.nextElementSibling !== this.watchRail) anchor.after(this.watchRail);
      }

      variantIdsForCard(card) {
        return [...card.querySelectorAll('[data-bogo-variant] option, input[data-bogo-variant]')]
          .map((option) => String(option.value || ''))
          .filter((id) => /^\d+$/.test(id));
      }

      indexRenderedCards(cards = this.renderedCards()) {
        cards.forEach((card) => {
          if (!card.dataset.bogoCollectionRank) {
            card.dataset.bogoCollectionRank = String(this.nextCollectionRank);
            this.nextCollectionRank += 1;
          }
          const productId = String(card.dataset.productId || '');
          if (productId) this.seenProductIds.add(productId);
          this.variantIdsForCard(card).forEach((variantId) => this.seenVariantIds.add(variantId));
          card.querySelectorAll('[id]').forEach((element) => {
            if (element.id) this.seenDomIds.add(element.id);
          });
        });
      }

      setupInfiniteScroll() {
        if (!this.sentinel || !this.nextLink || !('IntersectionObserver' in window)) return;
        this.infiniteObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage({ source: 'scroll' });
          },
          { rootMargin: '900px 0px', threshold: 0 }
        );
        this.infiniteObserver.observe(this.sentinel);
      }

      setupStickyBar() {
        if (!this.filters) return;
        this.stickyHeader = document.querySelector('.section-header') || document.querySelector('sticky-header');
        window.addEventListener('scroll', this.scheduleStickyOffsetBound, { passive: true });
        window.addEventListener('resize', this.scheduleStickyOffsetBound, { passive: true });
        if ('ResizeObserver' in window && this.stickyHeader) {
          this.stickyResizeObserver = new ResizeObserver(this.scheduleStickyOffsetBound);
          this.stickyResizeObserver.observe(this.stickyHeader);
        }
        this.syncStickyOffset();
      }

      scheduleStickyOffset() {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.syncStickyOffset();
        });
      }

      syncStickyOffset() {
        if (!this.filters) return;
        const rect = this.stickyHeader?.getBoundingClientRect();
        const visibleBottom = rect && rect.bottom > 0 ? Math.min(rect.bottom, window.innerHeight) : 0;
        this.filters.style.setProperty('--arista-bogo-filter-top', `${Math.max(0, Math.round(visibleBottom))}px`);
      }

      async ensureSortCoverage() {
        if (this.activeSort === 'recommended' || this.isEnsuringSort) return;
        const requestedSort = this.activeSort;
        this.isEnsuringSort = true;
        this.filters?.setAttribute('aria-busy', 'true');
        let attempts = 0;

        try {
          while (
            this.isConnected &&
            this.activeSort === requestedSort &&
            this.nextLink &&
            attempts < 50
          ) {
            attempts += 1;
            const result = await this.loadNextPage({ source: 'sort' });
            if (!result || result.error || (!result.loaded && !result.hasNext)) break;
          }
        } finally {
          this.isEnsuringSort = false;
          if (!this.isEnsuringFilter) this.filters?.removeAttribute('aria-busy');
          this.applyFilter();
          if (
            this.activeSort !== 'recommended' &&
            this.activeSort !== requestedSort &&
            this.nextLink
          ) {
            queueMicrotask(() => this.ensureSortCoverage());
          }
        }
      }

      async ensureFilterCoverage() {
        if (this.activeFilter === 'all' || this.isEnsuringFilter) return;
        const requestedFilter = this.activeFilter;
        this.isEnsuringFilter = true;
        this.filters?.setAttribute('aria-busy', 'true');
        let attempts = 0;

        try {
          while (
            this.isConnected &&
            this.activeFilter === requestedFilter &&
            this.visibleCards().length < this.filterTargetCount &&
            this.nextLink &&
            attempts < 50
          ) {
            attempts += 1;
            const result = await this.loadNextPage({ source: 'filter' });
            if (!result || result.error || (!result.loaded && !result.hasNext)) break;
          }
        } finally {
          this.isEnsuringFilter = false;
          if (!this.isEnsuringSort) this.filters?.removeAttribute('aria-busy');
          this.applyFilter();
          if (
            this.activeFilter !== 'all' &&
            this.activeFilter !== requestedFilter &&
            this.visibleCards().length < this.filterTargetCount &&
            this.nextLink
          ) {
            queueMicrotask(() => this.ensureFilterCoverage());
          }
        }
      }

      safeNextUrl(value) {
        try {
          const url = new URL(value, window.location.origin);
          if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return null;
          url.hash = '';
          return url;
        } catch (_error) {
          return null;
        }
      }

      async loadNextPage(options = {}) {
        if (this.loadingPromise) return this.loadingPromise;
        this.loadingPromise = this.performNextPageLoad(options);
        try {
          return await this.loadingPromise;
        } finally {
          this.loadingPromise = null;
        }
      }

      async performNextPageLoad({ source = 'scroll' } = {}) {
        if (this.isLoading || !this.nextLink || !this.grid) {
          return { loaded: 0, hasNext: Boolean(this.nextLink), error: false };
        }

        const nextUrl = this.safeNextUrl(this.nextLink.getAttribute('href'));
        if (!nextUrl || this.loadedUrls.has(nextUrl.href)) {
          this.completeInfiniteLoading();
          return { loaded: 0, hasNext: false, error: Boolean(!nextUrl) };
        }

        this.isLoading = true;
        this.loadedUrls.add(nextUrl.href);
        this.grid.setAttribute('aria-busy', 'true');
        this.nextLink.setAttribute('aria-busy', 'true');
        if (this.loadStatus) this.loadStatus.textContent = 'Loading more eligible pieces…';
        this.fetchController = new AbortController();

        let appendedCards = [];
        let loadSucceeded = false;
        try {
          const response = await fetch(nextUrl.href, {
            headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.fetchController.signal,
          });
          if (!response.ok) throw new Error(`Product page returned ${response.status}.`);

          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const escapedSectionId = CSS.escape(this.dataset.sectionId || '');
          const nextBuilder =
            parsed.querySelector(`arista-bogo-builder[data-section-id="${escapedSectionId}"]`) ||
            parsed.querySelector('arista-bogo-builder');
          const nextGrid = nextBuilder?.querySelector('[data-bogo-grid]');
          if (!nextGrid) throw new Error('The next product batch was unavailable.');

          const pendingProductIds = new Set();
          const pendingVariantIds = new Set();
          const pendingDomIds = new Set();
          const fragment = document.createDocumentFragment();

          [...nextGrid.querySelectorAll('.arista-bogo-card[data-product-id]')].forEach((sourceCard) => {
            const card = document.importNode(sourceCard, true);
            const productId = String(card.dataset.productId || '');
            const variantIds = this.variantIdsForCard(card);
            const domIds = [...card.querySelectorAll('[id]')].map((element) => element.id).filter(Boolean);
            const duplicateProduct = !productId || this.seenProductIds.has(productId) || pendingProductIds.has(productId);
            const duplicateVariant = variantIds.some(
              (variantId) => this.seenVariantIds.has(variantId) || pendingVariantIds.has(variantId)
            );
            const duplicateDomId = domIds.some(
              (id) => this.seenDomIds.has(id) || pendingDomIds.has(id) || Boolean(document.getElementById(id))
            );
            if (duplicateProduct || duplicateVariant || duplicateDomId) return;

            card.querySelectorAll('img').forEach((image) => {
              image.loading = 'lazy';
              image.removeAttribute('fetchpriority');
            });
            pendingProductIds.add(productId);
            variantIds.forEach((variantId) => pendingVariantIds.add(variantId));
            domIds.forEach((id) => pendingDomIds.add(id));
            appendedCards.push(card);
            fragment.appendChild(card);
          });

          this.grid.appendChild(fragment);
          this.indexRenderedCards(appendedCards);
          this.reconcileRenderedSelections(appendedCards);

          const nextNextLink = nextBuilder.querySelector('[data-bogo-next]');
          if (nextNextLink) {
            const validatedNextUrl = this.safeNextUrl(nextNextLink.getAttribute('href'));
            if (validatedNextUrl && !this.loadedUrls.has(validatedNextUrl.href)) {
              this.nextLink.setAttribute('href', `${validatedNextUrl.pathname}${validatedNextUrl.search}`);
              this.nextLink.removeAttribute('aria-busy');
            } else {
              this.completeInfiniteLoading();
            }
          } else {
            this.completeInfiniteLoading();
          }

          this.applyFilter({ announce: false });
          this.updateLoadStatus();
          loadSucceeded = true;
          return { loaded: appendedCards.length, hasNext: Boolean(this.nextLink), error: false };
        } catch (error) {
          if (error?.name !== 'AbortError') {
            this.loadedUrls.delete(nextUrl.href);
            if (this.loadStatus) {
              this.loadStatus.textContent = 'More pieces could not load automatically. Use Load more pieces to try again.';
            }
            this.setLiveMessage('More pieces could not load automatically. Use Load more pieces to try again.');
          }
          return { loaded: 0, hasNext: Boolean(this.nextLink), error: true };
        } finally {
          this.isLoading = false;
          this.grid?.removeAttribute('aria-busy');
          this.nextLink?.removeAttribute('aria-busy');
          this.fetchController = null;
          // Finalise exact tab counts only after loading has ended. During the
          // last fetch, nextLink is removed while isLoading is still true, so
          // zero-result tabs would otherwise keep their temporary ellipsis.
          if (!this.nextLink) this.applyFilter({ announce: false });
          if (this.emptyState) {
            this.emptyState.hidden = this.visibleCards().length > 0 || Boolean(this.nextLink);
          }

          if (loadSucceeded && appendedCards.length === 0 && this.nextLink && source === 'scroll') {
            requestAnimationFrame(() => this.loadNextPage({ source: 'scroll' }));
          }
        }
      }

      updateLoadStatus() {
        if (!this.loadStatus) return;
        const loadedCount = this.renderedCards().length;
        this.loadStatus.textContent = this.nextLink
          ? `${loadedCount} verified silver pieces loaded. More load as you scroll.`
          : `All ${loadedCount} eligible pieces are loaded.`;
      }

      completeInfiniteLoading() {
        this.infiniteObserver?.disconnect();
        this.nextLink?.remove();
        this.nextLink = null;
        if (this.loader && !this.loader.querySelector('.arista-bogo__load-complete')) {
          const complete = document.createElement('span');
          complete.className = 'arista-bogo__load-complete';
          complete.textContent = 'You have seen every eligible piece.';
          this.loader.insertBefore(complete, this.sentinel || null);
        }
        this.updateLoadStatus();
      }

      reconcileRenderedSelections(cards) {
        let selectionUpdated = false;
        cards.forEach((card) => {
          const selectionEntry = [...this.selected.entries()].find(
            ([, item]) => String(item.productId) === String(card.dataset.productId)
          );
          if (!selectionEntry) return;

          const [selectedId] = selectionEntry;
          const input = card.querySelector('[data-bogo-variant]');
          if (!input) return;
          if (input.tagName === 'SELECT') {
            const option = [...input.options].find(
              (candidate) =>
                candidate.value === selectedId && !candidate.disabled && candidate.dataset.available !== 'false'
            );
            if (!option) return;
            input.value = selectedId;
          } else if (input.value !== selectedId || input.disabled || input.dataset.available === 'false') {
            return;
          }

          const freshItem = this.getVariantData(card);
          if (!freshItem.id || !freshItem.available || String(freshItem.id) !== selectedId) return;
          this.selected.set(selectedId, freshItem);
          card.dataset.selectedVariant = selectedId;
          card.dataset.selected = 'true';
          this.updateButton(card, true);
          this.updateCardPrice(card, freshItem);
          selectionUpdated = true;
        });

        if (selectionUpdated) {
          this.persistSelection();
          this.updateState({ announce: false });
        }
      }

      getVariantData(card) {
        const input = card.querySelector('[data-bogo-variant]');
        const option = input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
        const price = Number(option?.dataset.price || 0);
        const available = Boolean(
          input && option && !input.disabled && !option.disabled && option.dataset.available !== 'false'
        );

        return {
          id: Number(input?.value),
          price,
          priceFormatted: option?.dataset.priceFormatted || this.money(price),
          title: card.dataset.productTitle || 'Selected piece',
          productId: card.dataset.productId || '',
          image: option?.dataset.image || card.dataset.productImage || '',
          url: card.dataset.productUrl || '',
          available,
          freshlyValidated: true,
          card,
        };
      }

      sanitizeStoredItem(savedItem) {
        const id = Number(savedItem?.id);
        const productId = String(savedItem?.productId || '');
        if (!Number.isSafeInteger(id) || id <= 0 || !/^\d+$/.test(productId)) return null;

        const title = String(savedItem?.title || 'Selected piece').trim().slice(0, 240) || 'Selected piece';
        const safeUrl = this.safeStoredUrl(savedItem?.url, { productPathOnly: true });
        const safeImage = this.safeStoredUrl(savedItem?.image);

        return {
          id,
          price: 0,
          priceFormatted: '',
          title,
          productId,
          image: safeImage,
          url: safeUrl,
          available: null,
          freshlyValidated: false,
          card: null,
        };
      }

      safeStoredUrl(value, { productPathOnly = false } = {}) {
        if (typeof value !== 'string' || value.length === 0 || value.length > 2000) return '';
        try {
          const url = new URL(value, window.location.origin);
          if (!['http:', 'https:'].includes(url.protocol)) return '';
          if (productPathOnly) {
            if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return '';
            return `${url.pathname}${url.search}`;
          }
          const trustedImageHost =
            url.origin === window.location.origin ||
            url.hostname === 'cdn.shopify.com' ||
            url.hostname === 'aristajewels.in' ||
            url.hostname === 'www.aristajewels.in' ||
            url.hostname.endsWith('.myshopify.com') ||
            url.hostname.endsWith('.shopifycdn.com');
          return trustedImageHost ? url.href : '';
        } catch (_error) {
          return '';
        }
      }

      persistSelection() {
        try {
          const items = [...this.selected.values()].slice(0, this.maxSelection).map((item) => ({
            id: item.id,
            price: item.price,
            priceFormatted: item.priceFormatted,
            title: item.title,
            productId: item.productId,
            image: item.image,
            url: item.url,
          }));
          const payload = {
            version: this.storageVersion,
            savedAt: Date.now(),
            collectionHandle: this.dataset.collectionHandle || 'buy1-get1',
            items,
          };
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
        } catch (_error) {
          // The current-page selection still works when session storage is unavailable.
        }
      }

      removeStoredSelection() {
        try {
          window.sessionStorage.removeItem(this.storageKey);
        } catch (_error) {
          // No cleanup is required when session storage is unavailable.
        }
      }

      restoreSelection() {
        try {
          const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || 'null');
          const expectedHandle = this.dataset.collectionHandle || 'buy1-get1';
          const age = Date.now() - Number(stored?.savedAt);
          const isFresh =
            stored &&
            stored.version === this.storageVersion &&
            stored.collectionHandle === expectedHandle &&
            Number.isFinite(age) &&
            age >= 0 &&
            age <= this.storageMaxAge;

          if (!isFresh || !Array.isArray(stored.items)) {
            this.removeStoredSelection();
            return;
          }

          stored.items.slice(0, this.maxSelection).forEach((savedItem) => {
            const safeSavedItem = this.sanitizeStoredItem(savedItem);
            if (!safeSavedItem || this.selected.has(String(safeSavedItem.id))) return;

            const card = [...this.querySelectorAll('[data-bogo-card]')].find(
              (candidate) => candidate.dataset.productId === safeSavedItem.productId
            );
            if (!card) {
              this.selected.set(String(safeSavedItem.id), safeSavedItem);
              return;
            }

            const input = card.querySelector('[data-bogo-variant]');
            if (!input) return;

            if (input.tagName === 'SELECT') {
              const matchingOption = [...input.options].find(
                (option) =>
                  option.value === String(safeSavedItem.id) &&
                  !option.disabled &&
                  option.dataset.available !== 'false'
              );
              if (!matchingOption) return;
              input.value = String(safeSavedItem.id);
            } else if (
              input.disabled ||
              input.dataset.available === 'false' ||
              input.value !== String(safeSavedItem.id)
            ) {
              return;
            }

            const freshItem = this.getVariantData(card);
            if (!freshItem.id || !freshItem.available || this.selected.has(String(freshItem.id))) return;

            this.selected.set(String(freshItem.id), freshItem);
            card.dataset.selectedVariant = String(freshItem.id);
            card.dataset.selected = 'true';
            this.updateButton(card, true);
            this.updateCardPrice(card, freshItem);
          });

          if (this.selected.size > 0) this.persistSelection();
          else this.removeStoredSelection();
        } catch (_error) {
          this.selected.clear();
          this.removeStoredSelection();
        }
      }

      toggleCard(card) {
        const currentId = card.dataset.selectedVariant;
        if (currentId && this.selected.has(currentId)) {
          this.removeItem(currentId);
          return;
        }

        if (this.selected.size >= this.maxSelection) {
          this.setLiveMessage('Your pair is full. Use Remove or Replace in the selected-pair summary.');
          this.dock?.querySelector('[data-bogo-replace]')?.focus();
          return;
        }

        const item = this.getVariantData(card);
        if (!item.id || !item.available) {
          this.setLiveMessage('This option is unavailable. Choose another option.');
          return;
        }

        this.selected.set(String(item.id), item);
        card.dataset.selectedVariant = String(item.id);
        card.dataset.selected = 'true';
        this.updateButton(card, true);
        this.persistSelection();
        this.updateState();
      }

      removeItem(id, { announce = true } = {}) {
        const item = this.selected.get(String(id));
        if (!item) return;

        this.selected.delete(String(id));
        const card = this.findRenderedCard(item);
        if (card) {
          delete card.dataset.selectedVariant;
          card.dataset.selected = 'false';
          this.updateButton(card, false);
        }

        if (this.selected.size > 0) this.persistSelection();
        else this.removeStoredSelection();
        this.updateState({ announce });
      }

      removeSlot(slotNumber, options = {}) {
        const item = [...this.selected.values()][slotNumber - 1];
        if (!item) return;

        const removedCardButton = this.findRenderedCard(item)?.querySelector('[data-bogo-select]');
        this.removeItem(item.id, options);
        const focusTarget = this.dock?.hidden
          ? removedCardButton
          : this.dock?.querySelector('[data-bogo-remove], [data-bogo-submit]');
        this.focusElement(focusTarget);
      }

      replaceSlot(slotNumber) {
        const item = [...this.selected.values()][slotNumber - 1];
        if (!item) return;

        this.removeItem(item.id, { announce: false });
        this.setProgress('Choose a replacement piece', 'Your pair is ready for another eligible piece.');

        const replacementButton = [...this.querySelectorAll('[data-bogo-select]:not(:disabled)')].find(
          (button) => button.getAttribute('aria-pressed') !== 'true'
        );
        if (replacementButton) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          try {
            replacementButton.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
          } catch (_error) {
            replacementButton.scrollIntoView();
          }
          this.focusElement(replacementButton);
        }
      }

      focusElement(element) {
        if (!element) return;
        try {
          element.focus({ preventScroll: true });
        } catch (_error) {
          element.focus();
        }
      }

      findRenderedCard(item) {
        return [...this.querySelectorAll('[data-bogo-card]')].find(
          (card) =>
            card.dataset.productId === String(item.productId) &&
            card.dataset.selectedVariant === String(item.id)
        );
      }

      updateSelectedVariant(card) {
        const oldId = card.dataset.selectedVariant;
        const nextItem = this.getVariantData(card);
        this.updateCardPrice(card, nextItem);

        const button = card.querySelector('[data-bogo-select]');
        if (button) button.disabled = !nextItem.available;
        if (!oldId) {
          if (this.activeSort === 'price-ascending' || this.activeSort === 'price-descending') {
            this.applyFilter({ announce: false });
          }
          return;
        }

        const previousEntries = [...this.selected.entries()];
        const duplicatesAnotherSelection = previousEntries.some(
          ([id]) => id !== String(oldId) && id === String(nextItem.id)
        );

        if (!nextItem.id || !nextItem.available || duplicatesAnotherSelection) {
          this.selected.delete(String(oldId));
          delete card.dataset.selectedVariant;
          card.dataset.selected = 'false';
          this.updateButton(card, false);
          if (this.selected.size > 0) this.persistSelection();
          else this.removeStoredSelection();
          this.updateState();
          return;
        }

        this.selected = new Map(
          previousEntries.map(([id, item]) =>
            id === String(oldId) ? [String(nextItem.id), nextItem] : [id, item]
          )
        );
        card.dataset.selectedVariant = String(nextItem.id);
        card.dataset.selected = 'true';
        this.updateButton(card, true);
        this.persistSelection();
        this.updateState();
        if (this.activeSort === 'price-ascending' || this.activeSort === 'price-descending') {
          this.applyFilter({ announce: false });
        }
      }

      updateCardPrice(card, item) {
        const price = card.querySelector('[data-bogo-price]');
        if (price && item.priceFormatted) price.textContent = item.priceFormatted;
        if (Number.isFinite(Number(item.price)) && Number(item.price) > 0) {
          card.dataset.bogoSortPrice = String(item.price);
        }
      }

      updateButton(card, selected) {
        const button = card.querySelector('[data-bogo-select]');
        const label = button?.querySelector('[data-bogo-button-label]');
        if (!button || !label) return;
        button.setAttribute('aria-pressed', String(selected));
        label.textContent = selected ? 'Selected — remove' : 'Select piece';
      }

      clearSelection() {
        this.selected.clear();
        this.removeStoredSelection();
        this.querySelectorAll('[data-bogo-card]').forEach((card) => {
          delete card.dataset.selectedVariant;
          card.dataset.selected = 'false';
          this.updateButton(card, false);
        });
        this.previousCount = 0;
        if (this.dock) this.dock.hidden = true;
        this.clearDockOffset();
      }

      updateState({ announce = true } = {}) {
        const items = [...this.selected.values()].slice(0, this.maxSelection);
        const count = items.length;
        const complete = count === this.maxSelection;

        if (this.meter) this.meter.style.width = `${Math.min(count / this.maxSelection, 1) * 100}%`;
        if (this.dock) this.dock.hidden = count === 0;
        if (this.count) this.count.textContent = `${count}/2 ${count === 1 ? 'piece' : 'pieces'} selected`;
        if (this.submit) {
          this.submit.disabled = !complete;
          this.submit.textContent = complete ? this.submitDefaultLabel : 'Select 1 more piece';
        }

        this.renderSlots(items);
        this.renderEstimatedSaving(items);

        if (count === 0) {
          this.setProgress(
            'Choose your first piece',
            'Add any two eligible pieces. The free piece is confirmed by Shopify in your cart.',
            announce
          );
          if (this.dockMessage) this.dockMessage.textContent = 'Choose two eligible pieces to complete a pair.';
        } else if (count === 1) {
          this.setProgress(
            'Select one more to avail Buy 1 Get 1',
            'Your next eligible piece completes the pair.',
            announce
          );
          if (this.dockMessage) this.dockMessage.textContent = 'Select one more eligible piece.';
        } else {
          this.setProgress(
            'Your pair is ready',
            'Add both pieces to cart. Shopify will confirm the BUY1GET1 allocation.',
            announce
          );
          if (this.dockMessage) this.dockMessage.textContent = 'Ready to add. Final savings are verified in cart.';
          if (announce && this.previousCount !== count) {
            this.launchConfetti();
            const estimatedSaving = this.estimatedSavingForItems(items);
            if (estimatedSaving > 0) this.showSavingCelebration(estimatedSaving);
          }
        }

        this.previousCount = count;
        if (count > 0) this.scheduleDockOffset();
        else this.clearDockOffset();
      }

      renderEstimatedSaving(items) {
        if (!this.estimatedSaving) return;
        const estimatedSaving = this.estimatedSavingForItems(items);
        if (estimatedSaving <= 0) {
          this.estimatedSaving.hidden = true;
          this.estimatedSaving.textContent = '';
          return;
        }

        this.estimatedSaving.textContent = `Estimated saving: ${this.money(estimatedSaving)}`;
        this.estimatedSaving.hidden = false;
      }

      estimatedSavingForItems(items) {
        if (items.length !== this.maxSelection || !items.every((item) => item.freshlyValidated === true)) return 0;
        const prices = items.map((item) => Number(item.price));
        if (prices.length !== this.maxSelection || prices.some((price) => !Number.isFinite(price) || price <= 0)) return 0;
        return Math.min(...prices);
      }

      showSavingCelebration(amount) {
        if (!this.celebration || !this.celebrationValue || !Number.isFinite(amount) || amount <= 0) return;
        if (this.celebrationTimer) window.clearTimeout(this.celebrationTimer);
        if (this.savingAnimationFrame) cancelAnimationFrame(this.savingAnimationFrame);

        this.celebration.hidden = false;
        this.celebrationValue.textContent = this.money(0);
        this.setLiveMessage(
          `Your pair is ready. Estimated saving ${this.money(amount)}. Shopify confirms the final saving in your cart.`
        );

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion) {
          this.celebrationValue.textContent = this.money(amount);
        } else {
          const duration = 900;
          const startedAt = performance.now();
          const animate = (timestamp) => {
            const progress = Math.min((timestamp - startedAt) / duration, 1);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            this.celebrationValue.textContent = this.money(Math.round(amount * easedProgress));
            if (progress < 1 && !this.celebration.hidden) {
              this.savingAnimationFrame = requestAnimationFrame(animate);
            } else {
              this.savingAnimationFrame = null;
            }
          };
          this.savingAnimationFrame = requestAnimationFrame(animate);
        }

        this.celebrationTimer = window.setTimeout(() => this.hideSavingCelebration(), 6500);
      }

      hideSavingCelebration() {
        if (this.celebrationTimer) window.clearTimeout(this.celebrationTimer);
        this.celebrationTimer = null;
        if (this.savingAnimationFrame) cancelAnimationFrame(this.savingAnimationFrame);
        this.savingAnimationFrame = null;
        if (this.celebration) this.celebration.hidden = true;
      }

      renderSlots(items) {
        if (!this.slotsContainer) return;
        this.slotsContainer.innerHTML = [0, 1].map((index) => this.slotMarkup(items[index], index + 1)).join('');
      }

      slotMarkup(item, slotNumber) {
        if (!item) {
          return `
            <div class="arista-bogo-slot arista-bogo-slot--empty" data-bogo-slot="${slotNumber}" role="listitem">
              <span class="arista-bogo-slot__number" aria-hidden="true">${slotNumber}</span>
              <span class="arista-bogo-slot__label">Choose ${slotNumber === 1 ? '1st' : '2nd'} piece</span>
            </div>
          `;
        }

        const image = item.image
          ? `<img class="arista-bogo-slot__media" src="${this.escapeHtml(item.image)}" alt="" width="44" height="44" loading="lazy">`
          : `<span class="arista-bogo-slot__media-placeholder" aria-hidden="true">${slotNumber}</span>`;
        const title = this.escapeHtml(item.title);
        const price = item.freshlyValidated && item.priceFormatted
          ? `<span class="arista-bogo-slot__price">${this.escapeHtml(item.priceFormatted)}</span>`
          : `<span class="arista-bogo-slot__price">Price checked when added</span>`;

        return `
          <div class="arista-bogo-slot arista-bogo-slot--filled" data-bogo-slot="${slotNumber}" role="listitem">
            ${image}
            <div class="arista-bogo-slot__details">
              <span class="arista-bogo-slot__title">${title}</span>
              ${price}
            </div>
            <div class="arista-bogo-slot__actions">
              <button type="button" class="arista-bogo-slot__action" data-bogo-replace="${slotNumber}" aria-label="Replace ${title}">Replace</button>
              <button type="button" class="arista-bogo-slot__action" data-bogo-remove="${slotNumber}" aria-label="Remove ${title}">Remove</button>
            </div>
          </div>
        `;
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }

      syncDockOffset() {
        if (!this.isConnected || !this.dock || this.dock.hidden) return;
        document.body?.style.setProperty(
          '--arista-bogo-dock-height',
          `${Math.ceil(this.dock.getBoundingClientRect().height)}px`
        );
        document.body?.classList.add('arista-bogo-dock-active');
        this.syncFloatingOffsets();
      }

      syncFloatingOffsets() {
        const candidates = [
          ...document.querySelectorAll(
            ".cta_group_preview, [id^='omega-cta-button-'], [class*='omega'][class*='floating'], [class*='ot-'][class*='floating']"
          ),
        ];
        const roots = candidates.filter(
          (candidate) => !candidates.some((other) => other !== candidate && other.contains(candidate))
        );

        roots.forEach((element) => {
          if (!(element instanceof HTMLElement)) return;
          if (element.classList.contains('arista-bogo-floating-offset')) return;
          const computedBottom = Number.parseFloat(window.getComputedStyle(element).bottom);
          element.style.setProperty(
            '--arista-bogo-floating-base-bottom',
            `${Number.isFinite(computedBottom) ? Math.max(0, computedBottom) : 0}px`
          );
          element.classList.add('arista-bogo-floating-offset');
        });
      }

      clearFloatingOffsets() {
        document.querySelectorAll('.arista-bogo-floating-offset').forEach((element) => {
          element.classList.remove('arista-bogo-floating-offset');
          element.style.removeProperty('--arista-bogo-floating-base-bottom');
        });
      }

      scheduleDockOffset() {
        if (this.dockFrame) cancelAnimationFrame(this.dockFrame);
        this.dockFrame = requestAnimationFrame(() => {
          this.dockFrame = null;
          this.syncDockOffset();
        });
      }

      clearDockOffset() {
        if (this.dockFrame) cancelAnimationFrame(this.dockFrame);
        this.dockFrame = null;
        document.body?.classList.remove('arista-bogo-dock-active');
        document.body?.style.removeProperty('--arista-bogo-dock-height');
        this.clearFloatingOffsets();
      }

      setProgress(title, message, announce = true, priority = 'polite') {
        if (this.statusTitle) this.statusTitle.textContent = title;
        if (this.message) this.message.textContent = message;
        if (announce) this.setLiveMessage(`${title}. ${message}`, priority);
      }

      setLiveMessage(message, priority = 'polite') {
        if (!this.live) return;
        this.live.setAttribute('aria-live', priority);
        this.live.textContent = message;
      }

      launchConfetti() {
        if (!this.confetti || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const colors = ['#9f5856', '#c68b89', '#c5a059', '#f6e2de', '#23201f'];
        this.confetti.replaceChildren();
        for (let index = 0; index < 24; index += 1) {
          const piece = document.createElement('span');
          piece.className = 'arista-bogo__confetti-piece';
          piece.style.left = `${Math.random() * 100}%`;
          piece.style.backgroundColor = colors[index % colors.length];
          piece.style.animationDelay = `${Math.random() * 0.25}s`;
          piece.style.setProperty('--confetti-drift', `${Math.random() * 18 - 9}rem`);
          this.confetti.appendChild(piece);
        }
        window.setTimeout(() => this.confetti?.replaceChildren(), 2300);
      }

      matchingDiscounts(cart) {
        const normalize = (title) => String(title || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
        const expected = normalize(this.dataset.discountTitle || 'BUY1GET1');
        const matches = [];

        (cart.items || []).forEach((item) => {
          const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
          allocations.forEach((allocation) => {
            const title = allocation.discount_application?.title || allocation.title;
            const amount = Number(allocation.amount || 0);
            if (normalize(title) === expected && amount > 0) matches.push({ title, amount });
          });
        });

        if (matches.length === 0) {
          (cart.cart_level_discount_applications || []).forEach((allocation) => {
            const amount = Number(allocation.total_allocated_amount || 0);
            if (normalize(allocation.title) === expected && amount > 0) {
              matches.push({ title: allocation.title, amount });
            }
          });
        }
        return matches;
      }

      money(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
            style: 'currency',
            currency: this.dataset.currency || 'INR',
            maximumFractionDigits: 0,
          }).format(cents / 100);
        } catch (_error) {
          return `₹${(cents / 100).toFixed(0)}`;
        }
      }

      createCartLinesUpdateEvent(items) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, cart) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || !cart?.currency) {
          deferred.reject(new Error('Cart data was unavailable after the add.'));
          return;
        }
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      }

      dispatchCartError(message, code) {
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'arista-bogo-builder',
              errors: message,
              message,
            });
          }
        } catch (_error) {
          // Optional subscribers must not block the visible recovery path.
        }
        try {
          const { CartErrorEvent } = window.StandardEvents || {};
          if (CartErrorEvent) this.dispatchEvent(new CartErrorEvent({ error: message, code }));
        } catch (_error) {
          // Standard Events are additive; the shopper-facing recovery still runs.
        }
      }

      hasRenderableCartSections(state, sectionIds) {
        return sectionIds.every((sectionId) => {
          const html = state?.sections?.[sectionId];
          return typeof html === 'string' && html.trim().length > 0;
        });
      }

      async fetchFreshCartSections(sectionIds, productId) {
        const cartUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
        cartUrl.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(cartUrl.toString(), {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!response.ok) throw new Error('Fresh cart sections were unavailable.');
        const renderedSections = await response.json();
        const state = { id: productId, sections: renderedSections };
        if (!this.hasRenderableCartSections(state, sectionIds)) {
          throw new Error('Fresh cart sections were incomplete.');
        }
        return state;
      }

      async addSelectedToCart() {
        if (!this.submit || this.submit.disabled || this.selected.size !== this.maxSelection) return;

        const returnFocusTarget = this.findRenderedCard([...this.selected.values()][0])?.querySelector('[data-bogo-select]');
        const originalLabel = this.submit.textContent;
        this.submit.disabled = true;
        this.submit.setAttribute('aria-busy', 'true');
        this.submit.textContent = 'Adding your pair…';

        let cartDrawer = null;
        let sections = ['cart-icon-bubble'];
        try {
          cartDrawer = document.querySelector('cart-drawer');
          const drawerSections = cartDrawer?.getSectionsToRender?.();
          if (Array.isArray(drawerSections) && drawerSections.length > 0) {
            sections = drawerSections.map((section) => section.id).filter(Boolean);
          }
        } catch (_error) {
          cartDrawer = null;
        }
        const items = [...this.selected.values()].map((variant) => ({
          id: variant.id,
          quantity: 1,
          properties: {
            _arista_bogo: 'true',
            _arista_bogo_source: 'builder',
          },
        }));
        let linesUpdateDeferred = null;
        try {
          linesUpdateDeferred = this.createCartLinesUpdateEvent(items);
        } catch (_error) {
          linesUpdateDeferred = null;
        }

        let response;
        let responseText = '';
        try {
          response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
          });
          responseText = await response.text();
        } catch (_error) {
          const message = 'The add-to-cart request could not be confirmed.';
          linesUpdateDeferred?.reject(new Error(message));
          this.dispatchCartError(message, 'SERVICE_UNAVAILABLE');
          this.setProgress(
            'We could not confirm the add-to-cart request',
            'Check your cart before trying again so the same pair is not added twice.',
            true,
            'assertive'
          );
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }

        let state = {};
        try {
          state = JSON.parse(responseText);
        } catch (_error) {
          // A successful add remains committed even if optional section HTML is unavailable.
        }

        if (!response.ok || state.status) {
          const message = state.description || state.message || 'Unable to add this pair.';
          linesUpdateDeferred?.reject(new Error(message));
          this.dispatchCartError(message, 'INVALID');
          this.setProgress('We could not add this pair', message, true, 'assertive');
          this.submit.removeAttribute('aria-busy');
          this.submit.textContent = originalLabel;
          this.submit.disabled = false;
          return;
        }

        this.clearSelection();
        try {
          const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!cartResponse.ok) throw new Error('Cart verification was unavailable.');
          const cart = await cartResponse.json();
          this.resolveCartLinesUpdate(linesUpdateDeferred, cart);

          const discounts = this.matchingDiscounts(cart);
          const confirmedSaving = discounts.reduce((total, discount) => total + discount.amount, 0);
          if (confirmedSaving > 0) {
            this.setProgress(
              'BUY1GET1 confirmed',
              `Confirmed BUY1GET1 savings in cart: ${this.money(confirmedSaving)}.`
            );
          } else {
            this.setProgress(
              'Your pieces are in the cart',
              'The BUY1GET1 discount is not confirmed yet. Check cart or checkout before paying.'
            );
          }

          try {
            await window.AristaAttribution?.syncCartAttributes?.({ force: true });
          } catch (_error) {
            // Attribution retries before checkout; it must never block the cart.
          }

          let drawerState = state;
          if (cartDrawer && !this.hasRenderableCartSections(drawerState, sections)) {
            drawerState = await this.fetchFreshCartSections(sections, items[0]?.id);
          }

          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              const cartUpdatePromise = publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'arista-bogo-builder',
                productVariantId: items[0]?.id,
                cartData: drawerState,
                skipCartSectionsRefresh: true,
              });
              cartUpdatePromise?.catch?.(() => {});
            }
          } catch (_error) {
            // Optional subscribers must not delay or replace the verified drawer.
          }

          if (cartDrawer && this.hasRenderableCartSections(drawerState, sections)) {
            if (returnFocusTarget) cartDrawer.setActiveElement(returnFocusTarget);
            cartDrawer.renderContents(drawerState);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
        } catch (_error) {
          linesUpdateDeferred?.reject(new Error('Cart verification was unavailable after the add.'));
          this.setProgress(
            'Your pieces were added to the cart',
            'Open your cart to confirm the BUY1GET1 allocation before checkout.',
            true,
            'assertive'
          );
          window.location.assign(window.routes?.cart_url || '/cart');
        } finally {
          this.submit.removeAttribute('aria-busy');
          this.submit.textContent = originalLabel;
          this.submit.disabled = true;
        }
      }
    }
  );
}
